/**
 * Questions history has listed for a thread, remembered beyond the 20 turns
 * `GET /threads` returns, so a repeat asked after the panel reopens still
 * gets its confirmation (#765, spec §8.2; Codex P2, rounds 19 and 20).
 *
 * What is remembered is server-derived only: the question as the server
 * stored it — minimized, `[PHONE]` in place of a number (§6.2) — reduced to
 * a hash of its normalized text. Text typed here is never written: it may
 * carry personal data the server has yet to redact, and a hash of it would
 * be a pseudonym, not a redaction. The memory lives in `sessionStorage` —
 * this tab, until it closes — per (organization, thread), capped. Complete
 * knowledge of a thread is the server's; an unavailable storage costs only
 * this memory, the on-screen checks still apply.
 */

const STORAGE_PREFIX = 'aica.assistant.asked.';
const MAX_KEYS_PER_THREAD = 500;

/** FNV-1a (32-bit) of the trimmed, whitespace-collapsed, lowercased text. */
export function askedQuestionKey(question: string): string {
  const normalized = question.trim().replace(/\s+/g, ' ').toLowerCase();
  let hash = 0x811c9dc5;
  for (let index = 0; index < normalized.length; index += 1) {
    hash ^= normalized.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

function storageKey(orgId: string, threadId: string): string {
  return `${STORAGE_PREFIX}${orgId}.${threadId}`;
}

function readKeys(orgId: string, threadId: string): string[] {
  try {
    const raw = window.sessionStorage.getItem(storageKey(orgId, threadId));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((key): key is string => typeof key === 'string')
      : [];
  } catch {
    return [];
  }
}

/**
 * Remember `questions` — as history lists them, never as typed — as asked in
 * the thread; the oldest keys go once the cap is reached.
 */
export function rememberAskedQuestions(
  orgId: string,
  threadId: string,
  questions: readonly string[]
): void {
  if (questions.length === 0) return;
  try {
    const keys = new Set(readKeys(orgId, threadId));
    let changed = false;
    for (const question of questions) {
      const key = askedQuestionKey(question);
      if (!keys.has(key)) {
        keys.add(key);
        changed = true;
      }
    }
    if (!changed) return;
    window.sessionStorage.setItem(
      storageKey(orgId, threadId),
      JSON.stringify([...keys].slice(-MAX_KEYS_PER_THREAD))
    );
  } catch {
    // Storage unavailable (private window, quota, blocked): nothing to remember with.
  }
}

/** Whether this tab remembers `question` as listed for the thread. */
export function wasQuestionAsked(
  orgId: string,
  threadId: string,
  question: string
): boolean {
  return readKeys(orgId, threadId).includes(askedQuestionKey(question));
}
